import type { BlockStateUnionType, BlockType } from '@unifyapps/defs/types/block';
import type { PageInternalVersion } from '@unifyapps/defs/types/page';
import { getAreDepsCreated } from '@unifyapps/defs/utils/getIsVersionMigrated';
import { useMemo } from 'react';
import { DependencyHelper } from '../../dependency/helpers/DependencyHelper';
import { BlockHelper } from '../../helpers/BlockHelper';
import { useDependencyGraphProvider } from '../context/DependencyGraphProvider';
import type { DependencyGraphContextType } from '../context/DependencyGraphProvider/types';
import { useInterfaceStore } from '../stores/InterfaceStore';

export function getBlockDynamicAndConditionalPaths({
  blockState,
  block,
  shouldUseBuiltDependencies,
  pageVersion,
  buildEntityDependency,
}: {
  blockState: BlockStateUnionType;
  block: BlockType;
  shouldUseBuiltDependencies?: boolean;
  pageVersion?: PageInternalVersion;
  buildEntityDependency: DependencyGraphContextType['buildEntityDependency'];
}) {
  const blockId = block.id ?? '';

  const basePaths = shouldUseBuiltDependencies
    ? {
        dynPaths: BlockHelper.getDynamicPaths(block),
        condPaths: BlockHelper.getConditionalPaths(block),
      }
    : (() => {
        const blockDeps = buildEntityDependency({ ...blockState, id: blockId }, 'blocks')[blockId];
        return {
          dynPaths: DependencyHelper.getDynamicPaths(blockDeps),
          condPaths: DependencyHelper.getConditionalPaths(blockDeps),
        };
      })();

  const visibilityPaths = getAreDepsCreated(pageVersion)
    ? {
        dynPaths: [],
        condPaths: [],
      }
    : (() => {
        const blockDeps = buildEntityDependency(
          {
            id: blockId,
            visibility: blockState.visibility,
            additional: blockState.additional,
          },
          'blocks',
        )[blockId];

        return {
          dynPaths: DependencyHelper.getDynamicPaths(blockDeps),
          condPaths: DependencyHelper.getConditionalPaths(blockDeps),
        };
      })();

  const mergedDynPaths = DependencyHelper.mergeDynamicPaths(
    basePaths.dynPaths ?? [],
    visibilityPaths.dynPaths ?? [],
  );

  const mergedCondPaths = DependencyHelper.mergeConditionalPaths(
    basePaths.condPaths ?? [],
    visibilityPaths.condPaths ?? [],
  );

  return { dynPaths: mergedDynPaths, condPaths: mergedCondPaths };
}

export function useBlockDynamicAndConditionalPaths({
  blockState,
  block,
}: {
  blockState: BlockStateUnionType;
  block: BlockType;
}) {
  const { shouldUseBuiltDependencies } = useInterfaceStore().hooks.page.useFlags() ?? {};
  const { _version } = useInterfaceStore().hooks.page.metadata.useDetails() ?? {};
  const { buildEntityDependency } = useDependencyGraphProvider();

  return useMemo(
    () =>
      getBlockDynamicAndConditionalPaths({
        blockState,
        block,
        shouldUseBuiltDependencies,
        pageVersion: _version,
        buildEntityDependency,
      }),
    [blockState, block, shouldUseBuiltDependencies, _version, buildEntityDependency],
  );
}
